class HelperService {
  debounce<T extends (...args: unknown[]) => Promise<void> | void>(
      func: T, 
      wait: number
  ) : T & { cancel: () => void } {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isRunning = false;
    let pendingArgs: Parameters<T> | null = null;

    const runFunc = async () => {
      if (pendingArgs === null) return;
      
      isRunning = true;
      const args = pendingArgs;
      pendingArgs = null;
      
      try {
        await func(...args);
      } finally {
        isRunning = false;
      }
      
      // If new calls came in while running, schedule them with proper debounce delay
      if (pendingArgs !== null) {
        timeoutId = setTimeout(() => {
          timeoutId = null;
          runFunc();
        }, wait);
      }
    };

    const debounced = ((...args: Parameters<T>) => {
      pendingArgs = args;
      
      // Clear existing timeout
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      // If currently running, the function will reschedule itself when done
      if (isRunning) {
        return;
      }
      
      // Schedule new execution
      timeoutId = setTimeout(() => {
        timeoutId = null;
        runFunc();
      }, wait);
    }) as T & { cancel: () => void };

    // Add cancel method for cleanup
    (debounced as T & { cancel: () => void }).cancel = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      pendingArgs = null;
    };

    return debounced;
  }

  throttle<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) & { cancel: () => void } {
    let lastStart = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pendingArgs: Parameters<T> | null = null;
    let running = false;

    const invoke = async (args: Parameters<T>) => {
      running = true;
      lastStart = Date.now();

      try {
        await func(...args);
      } finally {
        running = false;

        if (pendingArgs) {
          const args = pendingArgs;
          pendingArgs = null;
          schedule(args);
        }
      }
    };

    const schedule = (args: Parameters<T>) => {
      const now = Date.now();
      const remaining = wait - (now - lastStart);

      if (remaining <= 0 && !running) {
        invoke(args);
      } else {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          timer = null;
          if (!running) invoke(args);
          else pendingArgs = args;
        }, Math.max(remaining, 0));
      }
    };

    const throttled = (...args: Parameters<T>) => {
      if (running) {
        pendingArgs = args;
        return;
      }
      schedule(args);
    };

    throttled.cancel = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      pendingArgs = null;
      running = false;
    };

    return throttled;
  }

  public async getWakeLock(): Promise<WakeLockSentinel | null> {
    let wakeLock = null;
    // Use JS wake lock API to keep screen on during benchmark to avoid connection lost
    if ("wakeLock" in navigator && document.visibilityState === 'visible') {
      try {
        wakeLock = await navigator.wakeLock.request('screen');
      } catch (e) {
        console.warn('Failed to acquire wake lock:', e);
      }
    }
    return wakeLock;
  }
}

export const helperService = new HelperService();